/**
 * Client-record and care-plan redesign rollout controls.
 *
 * These database keys are deliberately positive opt-ins. Missing or unreadable
 * rows preserve the current production surfaces.
 */
export const CLIENT_CARE_REDESIGN_FLAG_DB_KEYS = {
  CLIENT_WORKSTATION_REDESIGN: 'client_workstation_redesign',
  CARE_PLAN_STAGED_COMPOSER: 'care_plan_staged_composer',
  CARE_PLAN_COMPOSER_STREAMLINED: 'care_plan_composer_streamlined',
  CARE_PLAN_MAINTAINED_REDESIGN: 'care_plan_maintained_redesign',
  CLIENT_CARE_DERIVATION_SURFACE: 'client_care_derivation_surface',
  CLIENT_CARE_ISLAND_ASSESSMENTS: 'client_care_island_assessments',
} as const;

export type ClientCareRedesignFlagKey = keyof typeof CLIENT_CARE_REDESIGN_FLAG_DB_KEYS;

export type ClientCareRedesignFlags = Record<ClientCareRedesignFlagKey, boolean>;

export type ClientCareRedesignFlagRow = {
  flag_key: string;
  tenant_id: string | null;
  enabled: boolean;
};

export const CLIENT_CARE_REDESIGN_FLAG_KEYS = Object.keys(
  CLIENT_CARE_REDESIGN_FLAG_DB_KEYS,
) as ClientCareRedesignFlagKey[];

const CLIENT_CARE_REDESIGN_DB_KEY_SET = new Set<string>(
  Object.values(CLIENT_CARE_REDESIGN_FLAG_DB_KEYS),
);

export function isTenantScopedClientCareRedesignFlag(flagKey: string): boolean {
  return CLIENT_CARE_REDESIGN_DB_KEY_SET.has(flagKey);
}

export const CLIENT_CARE_REDESIGN_FLAGS_DEFAULT: ClientCareRedesignFlags = {
  CLIENT_WORKSTATION_REDESIGN: false,
  CARE_PLAN_STAGED_COMPOSER: false,
  CARE_PLAN_COMPOSER_STREAMLINED: false,
  CARE_PLAN_MAINTAINED_REDESIGN: false,
  CLIENT_CARE_DERIVATION_SURFACE: false,
  CLIENT_CARE_ISLAND_ASSESSMENTS: false,
};

export const CLIENT_CARE_REDESIGN_FLAG_DESCRIPTIONS: Record<ClientCareRedesignFlagKey, string> = {
  CLIENT_WORKSTATION_REDESIGN:
    'Opt in to the redesigned client-record shell while retaining the current workstation as rollback.',
  CARE_PLAN_STAGED_COMPOSER:
    'Opt in to the staged care-plan composer while retaining the current composer as rollback.',
  CARE_PLAN_COMPOSER_STREAMLINED:
    'Opt in to the one-canvas live composer extract. Flag-off restores today’s monolith scroll. Shared Budget service rows, plan-version summary, and four Agree cards stay on; rolling those back is a revert, not this flag.',
  CARE_PLAN_MAINTAINED_REDESIGN:
    'Opt in to the redesigned maintained-plan workspace while retaining the current page as rollback.',
  CLIENT_CARE_DERIVATION_SURFACE:
    'Show plan → schedule → delivery derivation and coordinator-owned structural drift on the record. Default off. Does not auto-heal.',
  CLIENT_CARE_ISLAND_ASSESSMENTS:
    'Serve the assessments island from its route instead of the workstation view. Default off. Flag-off never disables access or RLS.',
};

export function resolveClientCareRedesignFlagsFromRows(
  tenantId: string | null | undefined,
  rows: readonly ClientCareRedesignFlagRow[] | null | undefined,
): ClientCareRedesignFlags {
  const resolved = { ...CLIENT_CARE_REDESIGN_FLAGS_DEFAULT };
  if (!tenantId) return resolved;

  for (const key of CLIENT_CARE_REDESIGN_FLAG_KEYS) {
    const dbKey = CLIENT_CARE_REDESIGN_FLAG_DB_KEYS[key];
    const tenantRow = rows?.find((row) => row.flag_key === dbKey && row.tenant_id === tenantId);
    resolved[key] = Boolean(tenantRow?.enabled ?? false);
  }

  return resolved;
}

export function shouldUseRedesignedClientCareSurface(
  flags: ClientCareRedesignFlags,
  surface:
    | 'client-workstation'
    | 'care-plan-composer'
    | 'care-plan-composer-streamlined'
    | 'maintained-care-plan'
    | 'derivation-surface'
    | 'island-assessments',
): boolean {
  if (surface === 'client-workstation') return flags.CLIENT_WORKSTATION_REDESIGN;
  if (surface === 'care-plan-composer') return flags.CARE_PLAN_STAGED_COMPOSER;
  if (surface === 'care-plan-composer-streamlined') return flags.CARE_PLAN_COMPOSER_STREAMLINED;
  if (surface === 'derivation-surface') return flags.CLIENT_CARE_DERIVATION_SURFACE;
  if (surface === 'island-assessments') return flags.CLIENT_CARE_ISLAND_ASSESSMENTS;
  return flags.CARE_PLAN_MAINTAINED_REDESIGN;
}

export type ClientWorkstationRollout = {
  delivery: 'legacy' | 'redesign';
  securityEnabled: true;
};

/** Flags select renderer/assembly only. Security is never disabled. */
export function resolveClientWorkstationRollout(
  flags: ClientCareRedesignFlags | 'unavailable' | null | undefined,
): ClientWorkstationRollout {
  if (flags === 'unavailable' || flags == null || !flags.CLIENT_WORKSTATION_REDESIGN) {
    return { delivery: 'legacy', securityEnabled: true };
  }
  return { delivery: 'redesign', securityEnabled: true };
}

export type ClientRecordEditContinuity = {
  clientId: string;
  url: string;
  draft: { expectedVersion: string; dirty: boolean };
};

export function continueClientRecordEditAfterFlagChange(input: {
  previousDelivery: 'legacy' | 'redesign';
  nextFlags: ClientCareRedesignFlags | 'unavailable';
  clientId: string;
  url: string;
  draft: { expectedVersion: string; dirty: boolean };
}): ClientRecordEditContinuity & ClientWorkstationRollout {
  const rollout = resolveClientWorkstationRollout(input.nextFlags);
  return {
    ...rollout,
    clientId: input.clientId,
    url: input.url,
    draft: { ...input.draft },
  };
}
